import { ROLE_VALUES } from "@noa/api-core";
import { sendMessage, whenSessionReady, useAddMessage, useAppStore, useSetChatbotState } from "@noa/client-store";
import { useEffect, useState } from "react";
import { MAX_INPUT_LENGTH } from "../constants";

/**
 * Owns the composer's text state and submits via the shared SSE `sendMessage`.
 * Auth-agnostic — each app supplies `isSignedIn` from its own Clerk binding.
 * Only the user message is added here; assistant bubbles arrive as SSE chunks.
 *
 * `onSend` runs once the message has a session — at once, or after the API's warmup — receiving its id; each
 * app uses it to seamlessly redirect Home → /conversation/:id on the first message.
 */
export function useSendChatMessage(isSignedIn: boolean, onSend?: (sessionId: string) => void) {
  const addMessage = useAddMessage();
  const setChatbotState = useSetChatbotState();

  const [content, setContent] = useState("");

  // A draft belongs to the conversation it was typed in, so drop it whenever the active session
  // changes — the "new conversation" button, or picking another conversation from the history.
  // Subscribing to the store rather than reacting to a navigation event means this holds however
  // the switch was initiated, and on mobile regardless of whether the screen was focused.
  useEffect(
    () =>
      useAppStore.subscribe((state, previous) => {
        if (state.sessionId !== previous.sessionId) setContent("");
      }),
    []
  );

  const canSubmit = !!content.trim().length && isSignedIn;
  const messageLengthLabel = `${content.length} / ${MAX_INPUT_LENGTH}`;

  const handleSendMessage = () => {
    if (!canSubmit) return;

    const message = content;
    setContent("");
    sendMessage(message);

    const created_at = new Date().toISOString();
    addMessage({ role: ROLE_VALUES.USER, content: message, message_id: "", created_at, used_sources: [], feedback: 0 });
    setChatbotState("sending_message");
    // After the API's warmup when it was asleep: the message waited for this session.
    void whenSessionReady().then((sessionId) => onSend?.(sessionId));
  };

  return { content, setContent, canSubmit, messageLengthLabel, handleSendMessage };
}
